'use client';

import { useEffect, type RefObject } from 'react';

export type UsePopoverDismissOptions = {
  open: boolean;
  onClose: () => void;
  /** Clicks / taps outside this subtree close the popover (capture phase). */
  rootRef: RefObject<HTMLElement | null>;
  enabled?: boolean;
};

/**
 * Shared popover / disclosure behavior: close on outside pointer and Escape.
 * Use capture so it runs before other handlers; safe for static export and plain hosting.
 */
export function usePopoverDismiss({ open, onClose, rootRef, enabled = true }: UsePopoverDismissOptions) {
  useEffect(() => {
    if (!enabled || !open) return;

    const onPointerDown = (e: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (!root.contains(target)) onClose();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, onClose, rootRef, enabled]);
}
